import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, apiFetch } from "@shared/lib/api";
import { useAuthStatus, useAuthActions } from "@shared/lib/auth";
import { Alert, Button, Card, Spinner } from "@shared/components/ui";
import { Logo } from "@shared/components/Logo";
import type { TwoFactorRequestResponse, TwoFactorVerifyResponse } from "@shared/lib/types";
import Lottie from "lottie-react";
import lightAnimation from "../animations/loading-light.json";
import darkAnimation from "../animations/loading-dark.json";
import { useTheme } from "@shared/lib/ThemeContext";
import { markWelcomeAsSeen } from "@shared/lib/welcomeSession";

const CODE_LENGTH = 6;
const TRUSTED_DEVICE_TOKEN_KEY = "cradleos_2fa_session";
const ANIMATION_DURATION = 8000; // ms to show animation after verification
const FADE_DURATION = 400;

export function TwoFactorVerifyPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { token, status } = useAuthStatus();
  const { signOut } = useAuthActions();
  const { isDark } = useTheme();

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [errors, setErrors] = useState<string[]>([]);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isRequestingInitialCode, setIsRequestingInitialCode] = useState(false);
  const [verified, setVerified] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasRequestedCode = useRef(false);

  // Get the original destination from location state
  const fromPath = (location.state as { from?: string })?.from ?? "/";

  // Stabilize navigate and fromPath in refs so timer effects don't reset
  // when React Router returns new references on re-render
  const navRef = useRef(nav);
  navRef.current = nav;
  const fromPathRef = useRef(fromPath);
  fromPathRef.current = fromPath;

  // Redirect if not authenticated
  useEffect(() => {
    if (status === "anonymous" && !token) {
      navRef.current("/login", { replace: true });
    }
  }, [status, token]);

  // Request initial OTP code on mount (with guards for StrictMode double-invoke)
  // Uses both a ref and state flag to prevent duplicate requests
  useEffect(() => {
    if (token && !hasRequestedCode.current && !isRequestingInitialCode) {
      hasRequestedCode.current = true;
      setIsRequestingInitialCode(true);
      requestCode().finally(() => setIsRequestingInitialCode(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isRequestingInitialCode]);

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const requestCode = async () => {
    if (!token) return;

    setResending(true);
    setErrors([]);

    try {
      const res = await apiFetch<TwoFactorRequestResponse>("/api/v1/auth/2fa/request", {
        method: "POST",
        token,
      });
      setEmailSentTo(res.data.email_sent_to);
      setResendCooldown(60); // 60 second cooldown
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.errors);
      } else if (err instanceof Error) {
        setErrors([err.message]);
      } else {
        setErrors(["Failed to send verification code"]);
      }
    } finally {
      setResending(false);
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, "").slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-advance to next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newDigits.every((d) => d) && newDigits.join("").length === CODE_LENGTH) {
      verifyCode(newDigits.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);

    if (pastedData.length === CODE_LENGTH) {
      const newDigits = pastedData.split("");
      setDigits(newDigits);
      // Focus last input
      inputRefs.current[CODE_LENGTH - 1]?.focus();
      // Auto-verify
      verifyCode(pastedData);
    }
  };

  const verifyCode = useCallback(
    async (code: string) => {
      if (!token) return;

      setVerifying(true);
      setErrors([]);

      try {
        const res = await apiFetch<TwoFactorVerifyResponse>("/api/v1/auth/2fa/verify", {
          method: "POST",
          body: { code },
          token,
        });

        // Store trusted device token in localStorage
        localStorage.setItem(TRUSTED_DEVICE_TOKEN_KEY, res.data.trusted_device_token);

        // Show Lottie animation before navigating
        setVerified(true);
      } catch (err) {
        if (err instanceof ApiError) {
          setErrors(err.errors);
        } else if (err instanceof Error) {
          setErrors([err.message]);
        } else {
          setErrors(["Verification failed"]);
        }
        // Clear inputs on error
        setDigits(Array(CODE_LENGTH).fill(""));
        inputRefs.current[0]?.focus();
      } finally {
        setVerifying(false);
      }
    },
    [token]
  );

  // Handle post-verification animation timing
  useEffect(() => {
    if (!verified) return;

    // Fade in the animation
    const fadeInTimer = setTimeout(() => setFadeIn(true), 50);

    // Mark welcome as seen so we skip the welcome page
    markWelcomeAsSeen();

    // After animation duration, fade out then navigate
    const fadeOutTimer = setTimeout(() => setFadeOut(true), ANIMATION_DURATION);
    const navTimer = setTimeout(() => {
      navRef.current(fromPathRef.current, { replace: true });
    }, ANIMATION_DURATION + FADE_DURATION);

    return () => {
      clearTimeout(fadeInTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(navTimer);
    };
  }, [verified]);

  const handleCancel = async () => {
    await signOut();
    nav("/login", { replace: true });
  };

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (verified) {
    return (
      <div
        className="min-h-screen bg-bg flex flex-col items-center justify-center px-4 transition-opacity"
        style={{
          opacity: fadeOut ? 0 : fadeIn ? 1 : 0,
          transitionDuration: `${FADE_DURATION}ms`,
        }}
      >
        <Lottie animationData={isDark ? darkAnimation : lightAnimation} loop className="w-96 h-96" />
        <Logo size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
        <Logo size="lg" className="mb-8" />
        <Card title="Verify your identity" className="w-full">
          <p className="text-sm text-muted mb-6">
            We've sent a verification code to{" "}
            <span className="font-medium text-foreground">{emailSentTo ?? "your email"}</span>.
            Enter the 6-digit code to continue.
          </p>

          <Alert items={errors} />

          <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={verifying}
                className="w-12 h-14 text-center text-2xl font-mono font-bold shadow-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-muted disabled:text-muted"
                autoFocus={index === 0}
              />
            ))}
          </div>

          <div className="flex flex-col gap-3">
            <Button
              type="button"
              onClick={() => verifyCode(digits.join(""))}
              disabled={verifying || digits.some((d) => !d)}
              className="w-full"
            >
              {verifying ? (
                <span className="flex items-center gap-2">
                  <Spinner /> Verifying...
                </span>
              ) : (
                "Verify"
              )}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={requestCode}
                disabled={resending || resendCooldown > 0}
                className="text-primary hover:underline disabled:text-muted disabled:no-underline"
              >
                {resending ? (
                  "Sending..."
                ) : resendCooldown > 0 ? (
                  `Resend code (${resendCooldown}s)`
                ) : (
                  "Resend code"
                )}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="text-muted hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <details className="text-sm text-muted">
              <summary className="cursor-pointer hover:text-foreground">
                Having trouble?
              </summary>
              <div className="mt-2 space-y-2">
                <p>
                  If you're not receiving the code, check your spam folder or try requesting a new
                  one.
                </p>
                <p>
                  If you've lost access to your email, contact your administrator for assistance.
                </p>
              </div>
            </details>
          </div>
        </Card>
      </div>
    </div>
  );
}
